import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type { SetQuestionOfficialCommentInput } from '../../domain/entities/content.entity';

const workspaceKey = (questionId: string) => [
  'content',
  'official-comment',
  questionId,
];

/**
 * A questão e o comentário juntos. `retry: false` porque a questão inexistente
 * é 404 legítimo, que a página mostra como estado próprio.
 */
export function useOfficialCommentWorkspace(questionId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: workspaceKey(questionId),
    queryFn: () => repository.getOfficialCommentWorkspace(questionId),
    retry: false,
    // Tela de operação: abrir de novo relê, para não oferecer "Despublicar"
    // num comentário que o pipefy já mudou, nem editar sobre texto velho.
    staleTime: 0,
  });
}

/**
 * Relê o workspace e **espera a resposta**: o card da questão só sai do modo
 * edição com o comentário e o enunciado salvos já na tela.
 */
export function useRefetchOfficialCommentWorkspace(questionId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.refetchQueries({ queryKey: workspaceKey(questionId) }),
    [queryClient, questionId],
  );
}

/**
 * Toda escrita do comentário muda o workspace (o card da ficha da questão),
 * o detalhe da questão (onde o comentário aparece em "Relações") e o preview
 * da prova (onde ele aparece no card).
 */
function useInvalidateOfficialComment(questionId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (commentId?: string) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKey(questionId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['content', 'item', questionId],
      });
      if (commentId) {
        void queryClient.invalidateQueries({
          queryKey: ['content', 'item', commentId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['content', 'items'] });
      void queryClient.invalidateQueries({
        queryKey: ['content', 'exam-preview'],
      });
    },
    [queryClient, questionId],
  );
}

export function useUnpublishOfficialComment(questionId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateOfficialComment(questionId);
  return useMutation({
    mutationFn: (commentId: string) =>
      repository.unpublishOfficialComment(questionId).then(() => commentId),
    onSuccess: (commentId) => invalidate(commentId),
  });
}

/** Republicar é o publish genérico do item, endereçado pelo comentário. */
export function usePublishOfficialComment(questionId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateOfficialComment(questionId);
  return useMutation({
    mutationFn: (commentId: string) => repository.publishContentItem(commentId),
    onSuccess: (item) => invalidate(item.id),
  });
}

/**
 * O `PUT` é estado final: texto vazio **remove** o comentário (o campo do
 * card avisa disso). A última gravação vence (decisão do plano: o
 * `audit` mostra as duas, e comparar o `updatedAt` só entra se acontecer).
 */
export function useSaveOfficialComment(questionId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateOfficialComment(questionId);
  return useMutation({
    mutationFn: (input: SetQuestionOfficialCommentInput) =>
      repository.setQuestionOfficialComment(questionId, input),
    onSuccess: (comment) => invalidate(comment?.id),
  });
}
